import { BrandMark } from './BrandMark.tsx';
import { useMemo, useState } from 'react';
import { DEFAULT_TIME_ZONE, isValidTimeZone } from '@autoday/core';
import { getStore } from '../store/appStore.ts';
import { Button, Field, Sheet, inputCls } from '../ui/kit.tsx';
import { errorText, toast } from '../ui/toast.tsx';
import { useLang } from '../i18n/i18n.ts';
import { LanguageSwitch } from './ThemeControls.tsx';

export function allTimeZones(): string[] {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    return ['Europe/Stockholm', 'Europe/Helsinki', 'Europe/Oslo', 'Europe/Copenhagen', 'Europe/Berlin', 'Europe/London', 'UTC'];
  }
}

/** Enhetens tidszon om den är giltig, annars Europe/Stockholm. Språket påverkar aldrig förslaget. */
export function deviceTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimeZone(tz) ? tz : null;
  } catch {
    return null;
  }
}

/**
 * Första start: språk FÖRST (Svenska / English med egna namn, så att den som inte läser svenska hittar
 * engelska), därefter enhetens tidszon som användaren bekräftar. Språkbytet gäller och sparas direkt,
 * innan tidszonen bekräftas. Förklarar de tre separata sakerna.
 */
export function FirstRun() {
  const store = getStore();
  const { t } = useLang();
  const device = deviceTimeZone();
  const suggested = device ?? DEFAULT_TIME_ZONE;
  const [tz, setTz] = useState(suggested);
  const zones = useMemo(() => {
    const all = allTimeZones();
    return all.includes(suggested) ? all : [suggested, ...all];
  }, [suggested]);

  async function confirm() {
    try {
      await store.replace((s) => ({ ...s, settings: { ...s.settings, timeZone: tz, timeZoneConfirmed: true } }));
      await store.updateDevice((d) => ({ ...d, firstRunDone: true }));
    } catch (e) {
      toast({ kind: 'error', text: errorText(e) });
    }
  }

  return (
    <Sheet
      open
      hideClose
      onOpenChange={() => undefined}
      title={t('Välkommen till AutoDay', 'Welcome to AutoDay')}
      footer={
        <Button variant="primary" onClick={() => void confirm()}>
          {t('Bekräfta och börja', 'Confirm and start')}
        </Button>
      }
    >
      <div className="grid gap-4 text-sm">
        <BrandMark size={64} />
        <LanguageSwitch autoFocus />
        <Field
          label={t('Tidszon', 'Time zone')}
          htmlFor="fr-tz"
          hint={
            device
              ? t(`Föreslagen från enheten: ${device}. Ändra om du bor eller arbetar i en annan zon.`, `Suggested from this device: ${device}. Change it if you live or work in another zone.`)
              : t('Enhetens tidszon kunde inte läsas. Föreslagen: Europe/Stockholm. Ändra om du bor eller arbetar i en annan zon.', 'The device time zone could not be read. Suggested: Europe/Stockholm. Change it if you live or work in another zone.')
          }
        >
          <select id="fr-tz" className={inputCls} value={tz} onChange={(e) => setTz(e.target.value)}>
            {zones.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </Field>
        <p className="text-sm text-muted">{t('Dina första planer börjar här. Bekräfta tidszonen så att aktiviteter och påminnelser hamnar rätt.', 'Your first plans start here. Confirm your time zone so activities and reminders appear at the right time.')}</p>
        <p className="text-xs text-muted">{t('Börja på den här enheten. Konto och synk är valfria.', 'Start on this device. An account and sync are optional.')}</p>
      </div>
    </Sheet>
  );
}
